import { useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { formatClock, formatMoney, isoToZonedWallClock, zonedWallClockToIso } from '../mirror/time';
import type { WireIdentity } from '../wire';

export interface BookSheetPrefill {
  startIso?: string | undefined;
  serviceId?: string | undefined;
  resourceId?: string | undefined;
  clientId?: string | undefined;
  guestName?: string | undefined;
  source?: 'staff' | 'walk_in';
}

/**
 * One booking form, used by all three ways a booking starts: the walk-in button
 * on Today, a tap on a slot in Calendar, and "Reschedule" on a task.
 *
 * It is one component because they are one operation. A separate walk-in form
 * would drift from the calendar form, and the third caller — the task list —
 * would then have to choose which drift to inherit.
 *
 * Note what the form does **not** collect: an end time, a duration, or a price.
 * The service supplies the first two on the server and the catalog supplies the
 * third (B7). The form shows the price it has mirrored, greyed, labelled as the
 * catalog's — never as a field.
 */
export function BookSheet({
  prefill,
  onDone,
  onCancel,
}: {
  prefill: BookSheetPrefill;
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();

  const services = useLive(() => runtime.mirror.services(), []);
  const resources = useLive(() => runtime.mirror.resources(), []);
  const clients = useLive(() => runtime.mirror.clients(), []);
  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const currency = session?.tenant.currency ?? 'USD';

  const [serviceId, setServiceId] = useState(prefill.serviceId ?? '');
  const [resourceId, setResourceId] = useState(prefill.resourceId ?? '');
  const [startIso, setStartIso] = useState(prefill.startIso ?? '');
  const [clientId, setClientId] = useState(prefill.clientId ?? '');
  const [guestName, setGuestName] = useState(prefill.guestName ?? '');
  const [guestPhone, setGuestPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const service = services.find((s) => s.service_id === serviceId);
  const eligibleResources =
    service === undefined
      ? resources
      : resources.filter((r) => service.resource_ids.includes(r.resource_id));
  const client = clients.find((c) => c.client_id === clientId);

  const submit = async (): Promise<void> => {
    if (service === undefined) return setError('Choose a service.');
    const resource = eligibleResources.find((r) => r.resource_id === resourceId);
    if (resource === undefined) return setError('Choose where it happens.');
    if (startIso === '') return setError('Choose a time.');

    const namedClient = client !== undefined;
    const walkIn = !namedClient && guestName.trim() !== '';
    if (!namedClient && !walkIn) return setError('Name the guest.');

    const identities: WireIdentity[] =
      guestPhone.trim() === '' ? [] : [{ identity_kind: 'phone', identity: guestPhone.trim() }];
    if (walkIn && identities.length === 0) {
      // `resolveGuest` in `core-api` refuses a booking with no way to reach the
      // guest, and says why. Refusing here as well means the owner finds out at
      // the counter rather than an hour later in the task list.
      return setError('A phone number or email is needed to book someone new.');
    }

    await actions.book({
      serviceId: service.service_id,
      serviceName: service.name,
      durationMin: service.duration_min,
      resourceId: resource.resource_id,
      resourceName: resource.name,
      startIso,
      source: prefill.source ?? (walkIn ? 'walk_in' : 'staff'),
      notes: notes.trim() === '' ? undefined : notes.trim(),
      clientName: namedClient ? (client?.display_name ?? '') : guestName.trim(),
      ...(namedClient ? { clientId } : { guest: { name: guestName.trim(), identities } }),
    });
    onDone();
  };

  return (
    <div className="sheet" role="dialog" aria-label="Book an appointment">
      <div className="sheet__panel">
        <h2>Book</h2>

        <label className="field">
          <span>Service</span>
          <select
            value={serviceId}
            onChange={(e) => {
              setServiceId(e.target.value);
              setResourceId('');
            }}
          >
            <option value="">Choose…</option>
            {services
              .filter((s) => s.active)
              .map((s) => (
                <option key={s.service_id} value={s.service_id}>
                  {s.name} · {s.duration_min} min
                </option>
              ))}
          </select>
        </label>

        {service !== undefined ? (
          <p className="catalog-price">
            {formatMoney(service.price_cents, currency)} · from the menu, not editable here
          </p>
        ) : null}

        <label className="field">
          <span>Where</span>
          <select value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
            <option value="">Choose…</option>
            {eligibleResources.map((r) => (
              <option key={r.resource_id} value={r.resource_id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Starts</span>
          <input
            type="datetime-local"
            value={isoToZonedWallClock(startIso, timezone)}
            onChange={(e) => setStartIso(zonedWallClockToIso(e.target.value, timezone))}
          />
          {startIso !== '' ? (
            <small>
              {formatClock(startIso, timezone)} in {timezone}
            </small>
          ) : null}
        </label>

        <label className="field">
          <span>Guest already in the book</span>
          <select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              if (e.target.value !== '') setGuestName('');
            }}
          >
            <option value="">Someone new…</option>
            {clients
              .filter((c) => !c.archived)
              .map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.display_name}
                </option>
              ))}
          </select>
        </label>

        {clientId === '' ? (
          <>
            <label className="field">
              <span>Name</span>
              <input value={guestName} onChange={(e) => setGuestName(e.target.value)} />
            </label>
            <label className="field">
              <span>Phone</span>
              <input
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                inputMode="tel"
              />
            </label>
          </>
        ) : null}

        <label className="field">
          <span>Notes</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </label>

        {error !== null ? <p className="error">{error}</p> : null}

        <div className="sheet__actions">
          <button type="button" className="button button--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="button button--primary" onClick={() => void submit()}>
            Book
          </button>
        </div>
      </div>
    </div>
  );
}
